import { ref, nextTick } from 'vue'
import { defineStore, storeToRefs } from 'pinia'
import { chatBot, createChat, getChat } from '@/services/api/chatbot-api'
import type { Message } from '@/types/chat'
import { useSettingStore } from '@/stores/setting'
import { MAP_VIEW } from '@/constant/menu.ts'
import { MAP_3D } from '@/constant/map.ts'
import { parseToHTML } from '@/services/chat/parser'
import { loadMars3dRuntime } from '@/services/mars3d/loadRuntime'

// The map-3d stores, tool registry, and mars3d service below are all
// dynamically imported instead of statically imported. They each pull in
// mars3d/Cesium (~8MB), and this store is instantiated eagerly by NavBar on
// every page load — a static import here would drag the entire mars3d
// bundle into the main entry chunk regardless of whether the chat bot ever
// touches the map.

export const useChatStore = defineStore('chat', () => {
  const messages = ref<Message[]>([])
  const isBottom = ref(true)
  const typingText = ref(false)
  const loadingAnswer = ref(false)
  const chatBotActive = ref(false)
  const settingStore = useSettingStore()
  const { currentMenu, mapMode } = storeToRefs(settingStore)
  const isNewMessage = ref(false)
  const loadingMessage = ref(false)
  const setTypingText = (value: boolean) => {
    typingText.value = value
  }

  const setIsBottom = (value: boolean) => {
    isBottom.value = value
  }

  const setLoadingAnswer = (value: boolean) => {
    loadingAnswer.value = value
  }
  const sessionId = ref<string | undefined>(undefined)
  const addMessage = (message: Message) => {
    messages.value.push(message)
    isNewMessage.value = true
  }

  const sendMessage = async ({
    message,
    projectUuid,
  }: {
    message: string
    projectUuid: string
  }) => {
    try {
      loadingAnswer.value = true
      loadingMessage.value = true
      // Add user message
      const userMessage: Message = {
        id: Date.now().toString(),
        content: parseToHTML(message),
        role: 'user',
        timestamp: Date.now(),
      }
      addMessage(userMessage)
      // TODO: Replace with your actual API call
      await chatBot({ text: message, session_id: sessionId.value || '' })
      const chatResponse = await getChat({ session_id: sessionId.value || '' })
      const assistantMessages = chatResponse.data.messages.filter(
        (msg: any) => msg.role === 'assistant',
      )
      const latestAssistantMessage = assistantMessages[assistantMessages.length - 1]

      const assistantMessage: Message = {
        id: latestAssistantMessage.id,
        role: 'assistant',
        timestamp: Date.now(),
        content:
          parseToHTML(latestAssistantMessage.message) ||
          'I have extracted the following filter from your message',
      }
      loadingMessage.value = false

      // Wait for next tick to ensure route change is complete
      await nextTick()
      addMessage(assistantMessage)
      if (assistantMessages[assistantMessages.length - 2]?.json_mode) {
        handleChatFunction(assistantMessages[assistantMessages.length - 2].message)
      }
    } catch (error) {
      console.error('Error sending message:', error)
      // Add error message
      const errorMessage: Message = {
        id: Date.now().toString(),
        content: parseToHTML('Sorry, there was an error processing your message.'),
        role: 'assistant',
        timestamp: Date.now(),
      }
      addMessage(errorMessage)
    } finally {
      loadingAnswer.value = false
      loadingMessage.value = false
    }
  }

  const clearMessages = () => {
    messages.value = []
  }
  const startNewChat = async () => {
    clearMessages()
    const response = await createChat()
    sessionId.value = response.data.id
  }
  const openChatBot = async (id: string | undefined) => {
    if (!sessionId.value) {
      clearMessages()
      if (id) {
        sessionId.value = id
        const chatResponse = await getChat({ session_id: sessionId.value || '' })
        chatResponse.data.messages.forEach((message: any) => {
          if (!message.json_mode) {
            messages.value.push({
              id: message.id,
              role: message.role,
              timestamp: message.timestamp,
              content: parseToHTML(message.message),
            })
          }
        })
        const assistantMessages = chatResponse.data.messages.filter(
          (msg: any) => msg.role === 'assistant',
        )
        if (assistantMessages[assistantMessages.length - 1]?.json_mode) {
          handleChatFunction(assistantMessages[assistantMessages.length - 1]?.message)
        }
      } else {
        const response = await createChat()
        sessionId.value = response.data.id
      }
    }
    if (chatBotActive.value) {
      chatBotActive.value = false
    } else {
      chatBotActive.value = true
      isNewMessage.value = false
    }
  }
  const handleChatFunction = (assistantMessage: string) => {
    let parsed: any
    try {
      parsed = JSON.parse(assistantMessage)
    } catch (e) {
      console.warn('⚠️ Failed to parse json_mode assistant message', e)
      return
    }

    // Backend has shipped multiple JSON shapes over time.
    // Normalize to: { function_name: string, parameter: object }
    const functionName: string | undefined =
      parsed?.function_name ||
      parsed?.functionName ||
      parsed?.json_message_content?.function_name ||
      parsed?.json_content?.function_name

    const functionParameter: any =
      parsed?.parameter ||
      parsed?.parameters ||
      parsed?.json_message_content?.parameter ||
      parsed?.json_message_content?.parameters ||
      parsed?.json_content?.parameter ||
      parsed?.json_content?.parameters

    if (!functionName) {
      console.warn('⚠️ json_mode message missing function_name', parsed)
      return
    }

    if (!functionParameter) {
      console.warn(`⚠️ json_mode message missing parameters for ${functionName}`, parsed)
      return
    }
    switch (functionName) {
      case 'line_of_sight':
        handleSightline(functionParameter)
        break
      case 'flythrough_animation':
        handleFlightPath(functionParameter)
        break
      case 'terrain_analysis':
        handleTerrainAnalysis(functionParameter)
        break
      case 'shadow_simulation':
        handleShadow(functionParameter)
        break
      case 'flood_simulation':
        handleFlood(functionParameter)
        break
      case 'zoom_animation':
        handleZoomAnimation(functionParameter)
        break
    }
  }

  const activateTool = async (toolKey: string) => {
    await loadMars3dRuntime()
    const [{ useMap3dStore }, { useLayerStore }, { useMap3dTools }] = await Promise.all([
      import('@/stores/map-3d'),
      import('@/stores/map-3d/layer-tileset/manage/list.ts'),
      import('@/composables/useMap3dTools'),
    ])
    const map3dStore = useMap3dStore()
    const { currentToolActive } = storeToRefs(map3dStore)
    const layerManagementStore = useLayerStore()
    const { extendControlContainer } = storeToRefs(layerManagementStore)
    const { getToolByKey } = useMap3dTools()
    const tool = getToolByKey(toolKey)
    if (currentToolActive.value?.key !== toolKey && tool) {
      currentToolActive.value?.destroy()
      currentToolActive.value = tool
      extendControlContainer.value = true
    }
  }

  const setupMap3dView = () => {
    currentMenu.value = MAP_VIEW
    mapMode.value = MAP_3D
  }

  const handleFlightPath = (functionParameter: any) => {
    setupMap3dView()

    setTimeout(async () => {
      await activateTool('flight-path')
      const { useFlightStore } = await import('@/stores/map-3d/graphic/custom/flight.ts')
      const flightStore = useFlightStore()
      const { modelProperties } = storeToRefs(flightStore)
      if (functionParameter.model) {
        const modelUrl = `/models/${functionParameter.model}`
        flightStore.currentModel = modelUrl
        flightStore.handleChangeModel(modelUrl)
      }
      modelProperties.value = {
        scale: functionParameter.scale || 1,
        heading: functionParameter.heading || 0,
        minimumPixelSize: functionParameter.minimum_pixel_size || 100,
      }
      flightStore.addFlightPath()
      flightStore.startPath()
    }, 1000)
  }

  const handleTerrainAnalysis = (functionParameter: any) => {
    setupMap3dView()
    setTimeout(async () => {
      await activateTool('dem-slope')
      const { useTerrainAnalysisStore } = await import(
        '@/stores/map-3d/thing/terrain/terrain-analysis'
      )
      const terrainAnalysisStore = useTerrainAnalysisStore()
      terrainAnalysisStore.init()
      const { Cesium } = (window as any).mars3d
      if (functionParameter.polygon) {
        const polygonPositions = functionParameter.polygon.map((point: any) =>
          Cesium.Cartesian3.fromDegrees(point[0], point[1]),
        )
        setTimeout(() => {
          terrainAnalysisStore.applyChat(
            polygonPositions,
            functionParameter.split_number,
            functionParameter.shading_type,
            false,
          )
        }, 500)
      }
    }, 1000)
  }

  const handleSightline = (functionParameter: any) => {
    setupMap3dView()
    setTimeout(async () => {
      await activateTool('los')
      const { useSightlineStore } = await import('@/stores/map-3d/thing/analysis/sightline.ts')
      const sightlineStore = useSightlineStore()
      sightlineStore.destroy()
      sightlineStore.init()
      const mars3d = (window as any).mars3d
      const Cesium = mars3d.Cesium
      if (functionParameter.start_point && functionParameter.end_point) {
        const centerPosition = Cesium.Cartesian3.fromDegrees(
          functionParameter.start_point.lng,
          functionParameter.start_point.lat,
          functionParameter.start_point.height_m,
        )
        const targetPosition = Cesium.Cartesian3.fromDegrees(
          functionParameter.end_point.lng,
          functionParameter.end_point.lat,
          functionParameter.end_point.height_m,
        )

        sightlineStore.createSightlineFromPoints(centerPosition, targetPosition, {
          offsetHeight: 2.0,
          showPoints: true,
        })
      } else if (functionParameter.center_point && functionParameter.radius_m) {
        const centerPosition = Cesium.Cartesian3.fromDegrees(
          functionParameter.center_point.lng,
          functionParameter.center_point.lat,
          functionParameter.center_point.height_m,
        )

        sightlineStore.createSightlineFromCircle(centerPosition, functionParameter.radius_m, {
          offsetHeight: 2.0,
          showPoints: true,
        })
      } else {
        sightlineStore.drawLine()
      }
    }, 1000)
  }

  const handleShadow = (functionParameter: any) => {
    setupMap3dView()
    setTimeout(() => {
      activateTool('shadow')
    }, 1000)
  }

  const handleFlood = (functionParameter: any) => {
    setupMap3dView()
    setTimeout(async () => {
      await activateTool('flood')
      const { useFloodSimulationStore } = await import(
        '@/stores/map-3d/thing/simulation/flood-simulation'
      )
      const floodStore = useFloodSimulationStore()
      floodStore.destroy()
      floodStore.init()
      if (
        functionParameter.polygon &&
        functionParameter.min_h != null &&
        functionParameter.max_h != null &&
        functionParameter.speed != null
      ) {
        setTimeout(() => {
          floodStore.setupFromChat(
            functionParameter.polygon,
            Number(functionParameter.min_h),
            Number(functionParameter.max_h),
            Number(functionParameter.speed),
          )
        }, 500)
      }
    }, 1000)
  }

  const handleZoomAnimation = (functionParameter: any) => {
    const zoomLevel = Number(functionParameter.level)
    const map_type = functionParameter.map_type || '3D'
    const zoom_point = functionParameter.zoom_point

    setupMap3dView()
    setTimeout(async () => {
      if (zoom_point && zoom_point.lng != null && zoom_point.lat != null) {
        await loadMars3dRuntime()
        const { Cesium } = (window as any).mars3d
        const position = Cesium.Cartesian3.fromDegrees(
          Number(zoom_point.lng),
          Number(zoom_point.lat),
          zoom_point.alt != null ? Number(zoom_point.alt) : 1500,
        )
        const { map } = await import('@/services/mars3d')
        map?.flyToPositions([position], {
          radius: 10000,
          duration: 2,
        })
      }
    }, 1000)
  }

  return {
    messages,
    isBottom,
    typingText,
    loadingAnswer,
    chatBotActive,
    sessionId,
    isNewMessage,
    setTypingText,
    setIsBottom,
    setLoadingAnswer,
    addMessage,
    sendMessage,
    clearMessages,
    openChatBot,
    loadingMessage,
    startNewChat,
  }
})
